import { useState } from 'react';
import { motion } from 'framer-motion';
import { Menu, X, LogOut } from 'lucide-react';
import { useAuth } from '../App';
import { ASSETS } from '../constants/site';

const LINKS = [
  ['Why GSLAB', '#why'],
  ['Compare', '#compare'],
  ['Automations', '#automations'],
  ['Portal', '#portal'],
  ['Process', '#process'],
  ['Pricing', '#pricing'],
  ['Engineer', '#engineer'],
];

export default function Nav() {
  const { user, login, logout } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <motion.header
      data-testid="nav-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[rgba(5,11,20,0.82)] border-b border-amber-500/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-[72px] flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src={ASSETS.icon} alt="GSLAB emblem" className="h-10 w-10 rounded-lg transition-transform duration-500 group-hover:rotate-[8deg]" />
          <span className="font-display font-800 font-extrabold text-xl tracking-tight">
            <span className="text-gradient-gold">GS</span><span className="text-white">LAB</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map(([label, href]) => (
            <a
              key={href}
              href={href}
              data-testid={href === '#pricing' ? 'nav-pricing-link' : `nav-${label.toLowerCase().replace(/\s/g, '-')}-link`}
              className="text-sm text-slate-300 hover:text-amber-300 transition-colors duration-300"
            >
              {label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <div className="flex items-center gap-3">
              <a href="/account" data-testid="nav-account-link" className="flex items-center gap-2 text-sm text-slate-200 hover:text-amber-300 transition-colors">
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-amber-500/40" />
                ) : (
                  <span className="h-8 w-8 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-300 text-xs font-bold">
                    {user.name?.[0] || 'U'}
                  </span>
                )}
                <span className="max-w-[120px] truncate">{user.name}</span>
              </a>
              <button data-testid="nav-logout-button" onClick={logout} className="text-slate-400 hover:text-red-400 transition-colors" title="Sign out">
                <LogOut size={17} />
              </button>
            </div>
          ) : (
            <button
              data-testid="nav-login-button"
              onClick={login}
              className="text-sm text-slate-300 hover:text-white border border-slate-600/60 hover:border-amber-500/50 rounded-full px-5 py-2 transition-colors duration-300"
            >
              Sign in with Google
            </button>
          )}
          <a
            data-testid="nav-consultation-button"
            href="#contact"
            className="text-sm font-semibold bg-gradient-to-r from-[#D4AF37] via-[#F3C64F] to-[#D4AF37] text-[#050B14] rounded-full px-5 py-2 hover:shadow-[0_0_24px_rgba(212,175,55,0.4)] transition-shadow duration-300"
          >
            Book a Consultation
          </a>
        </div>

        <button data-testid="nav-mobile-menu-button" className="lg:hidden text-slate-200" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-amber-500/10 bg-[rgba(5,11,20,0.97)] px-6 py-6 flex flex-col gap-4">
          {LINKS.map(([label, href]) => (
            <a key={href} href={href} onClick={() => setOpen(false)} className="text-slate-200 text-base">
              {label}
            </a>
          ))}
          {user ? (
            <button data-testid="nav-mobile-logout-button" onClick={logout} className="text-left text-red-400 text-base">Sign out ({user.name})</button>
          ) : (
            <button data-testid="nav-mobile-login-button" onClick={login} className="text-left text-amber-300 text-base">Sign in with Google</button>
          )}
          <a href="#contact" onClick={() => setOpen(false)} className="text-center font-semibold bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] text-[#050B14] rounded-full px-5 py-3">
            Book a Consultation
          </a>
        </div>
      )}
    </motion.header>
  );
}
